
import React, { useState } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, CheckCircle2, ListChecks, Brain, Info, CircleCheck, Image as ImageIcon, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useIsMobile } from '@/hooks/use-mobile';
import { 
  formatSectionContent, 
  processSpecialMarkers, 
  markdownToHtml,
  formatMemoryEnhanced
} from "@/utils/formatUtils";
import { ScrollArea } from "@/components/ui/scroll-area";

export const AdaptiveNoteContent = ({ content }: { content: string }) => {
  const [generatingImage, setGeneratingImage] = useState<{[key: string]: boolean}>({});
  const [generatedImages, setGeneratedImages] = useState<{[key: string]: string}>({});
  const [visualStyle, setVisualStyle] = useState<string>("educational");
  const { isMobile, screenSize } = useIsMobile();

  const cleanContent = (text: string) => {
    return text
      .replace(/\*\*/g, "")
      .replace(/\[RETRIEVE:(.*?)\]/g, "$1")
      .replace(/\[DC:(.*?):(.*?)\]/g, "$1 - $2")
      .replace(/\[CHUNK:(.*?)\]/g, "$1")
      .replace(/\\_/g, "_")
      // Remove duplicate emoji markers with their descriptions (specific patterns)
      .replace(/(🎵) ?Rhythm & Rhyme: Rhythm & Rhyme:/g, "$1 Rhythm & Rhyme:")
      .replace(/(🌟) ?Key Insight: Key Insight:/g, "$1 Key Insight:")
      .replace(/(🔠) ?Memory Acronym: Memory Acronym:/g, "$1 Memory Acronym:")
      .replace(/(🧠) ?Neural Connection: Neural Connection:/g, "$1 Neural Connection:")
      .replace(/(🎲) ?Pattern Recognition: Pattern Recognition:/g, "$1 Pattern Recognition:")
      .replace(/(🧩) ?Concept Map: Concept Map:/g, "$1 Concept Map:")
      // More generic pattern for other marker types
      .replace(/(🎯|🔄|🎭|🎪|🎮|🌟|📝|🔍|🧠|🔠|🎵|🎲|🕹️|🧩)([^:]*?):\s*\*?\*?([^:]*?)\1([^:]*?):\*?\*?/gi, "$1$2: ")
      // Remove duplicate marker text like "Quick Practice: Quick Practice Exercise:"
      .replace(/(🎯|🔄|🎭|🎪|🎮|🌟|📝|🔍|🧠|🔠|🎵|🎲|🕹️|🧩)([^:]*?):\s*\*\*\1([^*]*?)\*\*/gi, "$1$2: ")
      .replace(/(🎯|🔄|🎭|🎪|🎮|🌟|📝|🔍|🧠|🔠|🎵|🎲|🕹️|🧩)([^:]*?):\s*\*\*([^*]*?)(Exercise|Points|Bridge|Analogy|Challenge|Insight|Practice|Warning|Connection|Acronym|Pattern|Method|Map)\*\*/gi, "$1$2: ")
      // Remove duplicate words in marker labels
      .replace(/(Memory Acronym): Memory Acronym:/g, "$1:")
      .replace(/(Rhythm & Rhyme): Rhythm & Rhyme:/g, "$1:")
      .replace(/(Key Insight): Key Insight:/g, "$1:")
      .replace(/(Neural Connection): Neural Connection:/g, "$1:")
      .replace(/(Pattern Recognition): Pattern Recognition:/g, "$1:")
      .replace(/(Concept Map): Concept Map:/g, "$1:")
      .replace(/(Quick Practice): Quick Practice:/g, "$1:")
      .replace(/(Visual Analogy): Visual Analogy:/g, "$1:")
      .trim();
  };

  const formatSpecialContent = (text: string) => {
    // First apply the processSpecialMarkers for emojis
    let formatted = processSpecialMarkers(text);
    
    // Then apply memory enhancement formatting
    formatted = formatMemoryEnhanced(formatted);
    
    // Remove any remaining duplicate titles in the formatted content
    formatted = formatted
      .replace(/<span class="font-medium">(.*?)<\/span>\s*\*\*\1\*\*:/gi, '<span class="font-medium">$1</span>:')
      .replace(/<span class="font-medium">(.*?)<\/span>\s*\*\*(.*?)\*\*:/gi, '<span class="font-medium">$1</span>:')
      .replace(/<span class="font-medium">(.*?)<\/span>\s*\1:/gi, '<span class="font-medium">$1</span>:')
      .replace(/<span class="emoji">([^<]+)<\/span>\s*<span class="font-medium">([^<]+)<\/span>:\s*\2:/gi, 
               '<span class="emoji">$1</span><span class="font-medium">$2</span>:');
    
    return formatted;
  };

  const generateDiagram = async (title: string, concept: string) => {
    try {
      setGeneratingImage(prev => ({ ...prev, [title]: true }));
      
      const { data, error } = await supabase.functions.invoke('generate-note-visuals', {
        body: { 
          concept,
          style: visualStyle
        }
      });

      if (error) throw error;
      
      if (data.image) {
        setGeneratedImages(prev => ({ ...prev, [title]: data.image }));
        toast.success('Visualization generated successfully');
      }
    } catch (error) {
      console.error('Error generating visualization:', error);
      toast.error('Failed to generate visualization');
    } finally {
      setGeneratingImage(prev => ({ ...prev, [title]: false }));
    }
  };

  const renderSection = (title: string, icon: React.ReactNode, content: React.ReactNode, color: string) => {
    const cleanTitle = title.replace(/\[(.*?)\]/, '').trim();
    const isVisual = title.toLowerCase().includes('[visual]');
    
    return (
      <div className={cn(
        "p-1.5 sm:p-4 rounded-lg border border-gray-100 shadow-sm mb-2 sm:mb-4 transition-all duration-300",
        "bg-gradient-to-r hover:shadow-md",
        color
      )}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1.5 sm:mb-3 gap-1 sm:gap-2">
          <div className="flex items-center gap-1.5">
            <div className="p-1 sm:p-2 rounded-lg bg-white/90 shadow-sm">{icon}</div>
            <h3 className={`font-semibold ${isMobile ? "text-xs sm:text-sm" : "text-base sm:text-lg"} bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent`}>
              {cleanTitle}
            </h3>
          </div>
          {isVisual && !generatedImages[title] && (
            <div className="flex items-center gap-1 sm:gap-2 mt-0 sm:mt-0 ml-7">
              <select 
                className="text-[10px] sm:text-xs border-primary/20 text-primary bg-white rounded-md px-1 sm:px-1.5 py-0.5"
                value={visualStyle}
                onChange={(e) => setVisualStyle(e.target.value)}
              >
                <option value="educational">Educational</option>
                <option value="mnemonic">Mnemonic</option>
                <option value="cognitive">Cognitive Map</option>
              </select>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => generateDiagram(title, cleanTitle)}
                disabled={generatingImage[title]}
                className="text-[10px] sm:text-xs border-primary/20 text-primary hover:bg-primary/5 h-5 sm:h-7 px-1 sm:px-1.5"
              >
                {generatingImage[title] ? (
                  <>
                    <Loader2 className="mr-0.5 sm:mr-1 h-2.5 w-2.5 sm:h-3 sm:w-3 animate-spin" />
                    <span className="hidden sm:inline">Generating...</span>
                    <span className="sm:hidden">...</span>
                  </>
                ) : (
                  <>
                    <ImageIcon className="mr-0.5 sm:mr-1 h-2.5 w-2.5 sm:h-3 sm:w-3" />
                    <span className="hidden sm:inline">Generate Diagram</span>
                    <span className="sm:hidden">Generate</span>
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {generatedImages[title] && (
          <div className="mb-2 sm:mb-4 rounded-lg overflow-hidden border border-gray-100">
            <img 
              src={generatedImages[title]} 
              alt={`Diagram for ${cleanTitle}`}
              className="w-full h-auto object-contain bg-white"
            />
          </div>
        )}

        {content}
      </div>
    );
  };

  const renderContentBlock = (text: string, icon?: React.ReactNode) => {
    // Clean the text first to remove duplications
    const cleanedText = cleanContent(text);
    
    // Check if text contains any special markers that need to be processed
    const hasSpecialMarkers = /[🎯🔄🎭🎪🎮🌟📝🔍🧠🔠🎵🎲🕹️🧩]/.test(cleanedText) || 
                            /\[(DC|CHUNK|INTERLEAVE|SR|ELABORATE|RETRIEVE|DUAL_NBACK|BIAS)/.test(cleanedText);

    if (hasSpecialMarkers) {
      // For special content, use dangerouslySetInnerHTML with formatted content
      return (
        <div className="bg-white/80 p-1 sm:p-3 rounded-lg mb-1.5 sm:mb-3 shadow-sm hover:bg-white/90 transition-all duration-200">
          {icon && <div className="mt-0.5 mb-0.5 sm:mb-1">{icon}</div>}
          <div 
            className={`text-gray-700 leading-relaxed ${isMobile ? "text-[10px] sm:text-xs" : "text-sm"}`}
            dangerouslySetInnerHTML={{ __html: formatSpecialContent(cleanedText) }} 
          />
        </div>
      );
    }

    // For regular content, use the standard rendering approach
    return (
      <div className="flex items-start gap-1.5 sm:gap-3 bg-white/80 p-1 sm:p-3 rounded-lg mb-1.5 sm:mb-3 shadow-sm hover:bg-white/90 transition-all duration-200">
        {icon && <div className="mt-0.5">{icon}</div>}
        <div className={`text-gray-700 leading-relaxed ${isMobile ? "text-[10px] sm:text-xs" : "text-sm"}`}>{cleanedText}</div>
      </div>
    );
  };

  // Handle empty content
  if (!content) {
    return (
      <div className="p-4 text-center text-muted-foreground text-xs sm:text-sm">
        No content available for this note.
      </div>
    );
  }

  const sections = content.split("#").filter(Boolean);

  return (
    <ScrollArea className="space-y-2 sm:space-y-6">
      {sections.map((section, idx) => {
        const [title, ...content] = section.split("\n").filter(Boolean);
        const cleanTitle = title.trim();

        let icon = <BookOpen className={`h-2.5 w-2.5 sm:h-5 sm:w-5 ${isMobile ? 'text-primary/80' : 'text-primary'}`} />;
        let gradientColor = "from-purple-50 to-indigo-50";

        if (cleanTitle.toLowerCase().includes("quick check")) {
          icon = <CheckCircle2 className={`h-2.5 w-2.5 sm:h-5 sm:w-5 ${isMobile ? 'text-emerald-500' : 'text-emerald-600'}`} />;
          gradientColor = "from-emerald-50 to-green-50";
        } else if (cleanTitle.toLowerCase().includes("quiz")) {
          icon = <ListChecks className={`h-2.5 w-2.5 sm:h-5 sm:w-5 ${isMobile ? 'text-amber-500' : 'text-amber-600'}`} />;
          gradientColor = "from-amber-50 to-yellow-50";
        } else if (cleanTitle.toLowerCase().includes("visual")) {
          icon = <Brain className={`h-2.5 w-2.5 sm:h-5 sm:w-5 ${isMobile ? 'text-blue-500' : 'text-blue-600'}`} />;
          gradientColor = "from-blue-50 to-cyan-50";
        }

        return (
          <div key={idx} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.1}s` }}>
            {renderSection(
              cleanTitle,
              icon,
              <div className="space-y-1 sm:space-y-2">
                {content.map((item, i) => (
                  <React.Fragment key={i}>
                    {item.startsWith("-") ? (
                      renderContentBlock(item.slice(1), 
                        <CircleCheck className={`h-2 w-2 sm:h-4 sm:w-4 ${isMobile ? 'text-primary/80' : 'text-primary'}`} />
                      )
                    ) : (
                      item.trim() && renderContentBlock(item)
                    )}
                  </React.Fragment>
                ))}
              </div>,
              gradientColor
            )}
          </div>
        );
      })}
    </ScrollArea>
  );
};
